import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import type { Falha, Resultado } from './acesso';
import { buscar } from './buscar';
import { pedirLancados } from './lancados-no-periodo';
import type { LancadosNoPeriodo } from './lancados-no-periodo';

/**
 * BLOCO 4 — o front de /api/produtos (tela 28).
 *
 * Dinheiro trafega como string decimal com PONTO ('9.90'); a tela mostra e aceita vírgula
 * ('R$ 9,90'). Nenhuma conta de dinheiro acontece aqui: quem valida e decide é o servidor.
 */

export type FiltroDeProdutos =
  | 'todos'
  | 'ativos'
  | 'inativos'
  | 'vitaminas'
  | 'com_premiacao'
  | 'pendencias'
  | 'sem_configuracao';

export type FaixaGravada = {
  readonly ordem: number;
  readonly precoMinimo: string;
  readonly premio: string;
};

export type VersaoDaVitamina = {
  readonly versao: number;
  readonly ativa: boolean;
  readonly faixas: readonly FaixaGravada[];
  readonly motivo: string | null;
  readonly origem: 'inovapay' | 'migracao_1_0';
  readonly criadoEm: string;
  readonly criadoPor: string | null;
};

export type ProdutoComercial = {
  /** Unidades lançadas na vitamina no período do filtro (só quando a tela manda o período). */
  readonly lancadosNoPeriodo?: number;
  readonly id: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly grupo: string | null;
  /** Classificação do HOS (VITAMINAS, ALIMENTOS SUPLEMENTO…). */
  readonly classificacao: string | null;
  /** PMC do HOS — o teto de venda (dono, 28/09). */
  readonly pmc: string | null;
  /** PMC digitado no cadastro — teto só quando o HOS não tem PMC. */
  readonly pmcCadastro: string | null;
  /** Preço base — a base do prêmio do pré-vencido e do paradão. */
  readonly precoBase: string | null;
  readonly noParadao: boolean;
  readonly lotesAtivos: number;
  readonly custo: string | null;
  readonly estoque: string | null;
  readonly vistoNaOrigemEm: string | null;
  readonly vitamina: VersaoDaVitamina | null;
  readonly pendencias: readonly ('faixa_sem_premio' | 'sem_pmc')[];
};

export type DetalheDoProduto = ProdutoComercial & {
  readonly historico: readonly VersaoDaVitamina[];
};

export type ListaDeProdutos = {
  readonly itens: readonly ProdutoComercial[];
  readonly total: number;
  readonly contagens: {
    readonly catalogo: number;
    readonly configurados: number;
    readonly vitaminas: number;
    readonly incompletos: number;
    readonly semConfiguracao: number;
    readonly semPmc: number;
  };
  readonly grupos: readonly string[];
  readonly classificacoes: readonly string[];
  readonly fabricantes: readonly string[];
  readonly pagina: number;
  readonly tamanhoDaPagina: number;
};
export type OrdemDosProdutos =
  | 'recentes'
  | 'descricao'
  | 'estoque_maior'
  | 'estoque_menor'
  | 'pmc_maior'
  | 'pmc_menor'
  | 'base_maior'
  | 'base_menor'
  | 'custo_maior'
  | 'custo_menor';

export type ProblemaDaConfiguracao = {
  readonly nivel: 'erro' | 'aviso';
  readonly codigo: string;
  readonly faixa: number | null;
  readonly mensagem: string;
};

export type FalhaAoSalvar =
  | Falha
  | {
      readonly tipo: 'recusada';
      readonly problemas: readonly ProblemaDaConfiguracao[];
      readonly mensagem: string;
    };

async function pedir<T>(caminho: string, init: RequestInit = {}): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, { credentials: 'same-origin', ...init });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  let mensagem = '';
  try {
    mensagem = ((await r.clone().json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // corpo não é JSON
  }
  if (r.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (r.status === 400) return { ok: false, falha: { tipo: 'invalido', mensagem } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

export function listarProdutos(q: {
  filtro: FiltroDeProdutos;
  busca: string;
  grupo: string;
  classificacao?: string;
  fabricante?: string;
  ordem?: OrdemDosProdutos;
  comEstoque?: boolean;
  pagina: number;
  lancados?: LancadosNoPeriodo | null | undefined;
}) {
  const p = new URLSearchParams({ filtro: q.filtro, pagina: String(q.pagina) });
  pedirLancados(p, q.lancados);
  if (q.busca.trim()) p.set('busca', q.busca.trim());
  if (q.grupo) p.set('grupo', q.grupo);
  if (q.classificacao) p.set('classificacao', q.classificacao);
  if (q.fabricante) p.set('fabricante', q.fabricante);
  if (q.ordem && q.ordem !== 'recentes') p.set('ordem', q.ordem);
  if (q.comEstoque) p.set('comEstoque', '1');
  return pedir<ListaDeProdutos>(`/api/produtos?${p.toString()}`);
}

export const detalharProduto = (id: string) => pedir<DetalheDoProduto>(`/api/produtos/${id}`);

export async function salvarVitamina(
  sessao: Sessao,
  id: string,
  corpo: {
    ativa: boolean;
    faixas: { precoMinimo: string; premio: string }[];
    versaoEsperada: number;
  },
): Promise<
  | { ok: true; dados: { versao: number; avisos: readonly ProblemaDaConfiguracao[] } }
  | { ok: false; falha: FalhaAoSalvar }
> {
  let r: Response;
  try {
    r = await fetch(`/api/produtos/${id}/vitamina`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok)
    return {
      ok: true,
      dados: (await r.json()) as { versao: number; avisos: ProblemaDaConfiguracao[] },
    };
  if (r.status === 400) {
    try {
      const e = (
        (await r.json()) as {
          error?: { message?: string; details?: { problemas?: ProblemaDaConfiguracao[] } };
        }
      ).error;
      if (e?.details?.problemas)
        return {
          ok: false,
          falha: { tipo: 'recusada', problemas: e.details.problemas, mensagem: e.message ?? '' },
        };
      return { ok: false, falha: { tipo: 'invalido', mensagem: e?.message ?? '' } };
    } catch {
      return { ok: false, falha: { tipo: 'invalido', mensagem: '' } };
    }
  }
  if (r.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem: '' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

/** '9.90' → 'R$ 9,90'. Sem arredondar nada: a string já vem do servidor com as casas certas. */
export function reais(valor: string | null): string {
  if (valor === null) return '—';
  const [i = '0', d = ''] = valor.split('.');
  const inteiro = i.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${inteiro},${d.padEnd(2, '0')}`;
}

/** Prêmio curto da lista: '2.00' → 'R$ 2'; '2.50' → 'R$ 2,50'. */
export function reaisCurto(valor: string | null): string {
  if (valor === null) return '—';
  const [i = '0', d = ''] = valor.split('.');
  return /^0*$/.test(d) ? `R$ ${i}` : `R$ ${i},${d.padEnd(2, '0')}`;
}

/** O que a pessoa digitou ('9,9', 'R$ 9,90', '9.90') → '9.90', ou null se não é dinheiro. */
export function lerReais(texto: string): string | null {
  const limpo = texto.replace(/R\$\s?/i, '').replace(/\s/g, '');
  if (!limpo) return null;
  const normal = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  if (!/^\d{1,13}(\.\d{1,2})?$/.test(normal)) return null;
  const [i = '0', d = ''] = normal.split('.');
  return `${String(Number(i))}.${d.padEnd(2, '0')}`;
}

/** '9.90' → '9,90' — o valor dentro do campo, sem o "R$". */
export function paraCampo(valor: string): string {
  const [i = '0', d = ''] = valor.split('.');
  return `${i},${d.padEnd(2, '0')}`;
}

/* ------------------------------------------------------------------ *
 * Prêmio base por produto (pré-vencido e paradão) — vale da próxima semana (sábado)
 * ------------------------------------------------------------------ */

export type ProgramaPorBase = 'pre_vencido' | 'paradao';
export type PremioNoPrograma = {
  readonly programa: ProgramaPorBase;
  readonly doPrograma: string;
  readonly vigente: string | null;
  readonly agendado: { readonly premio: string | null; readonly desde: string } | null;
  readonly versao: number;
  readonly historico: readonly {
    readonly versao: number;
    readonly premio: string | null;
    readonly desde: string;
    readonly origem: string;
    readonly motivo: string;
    readonly por: string | null;
    readonly em: string;
  }[];
};

export async function lerPremiosDoProduto(
  id: string,
): Promise<Resultado<{ hoje: string; programas: readonly PremioNoPrograma[] }>> {
  let r: Response;
  try {
    r = await buscar(`/api/produtos/${id}/premio-base`, { credentials: 'same-origin' });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok)
    return {
      ok: true,
      dados: (await r.json()) as { hoje: string; programas: PremioNoPrograma[] },
    };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

/** Grava a exceção. `offline` aqui é INCERTO: o pedido pode ter chegado — recarregar e conferir. */
export async function definirPremioDoProduto(
  sessao: Sessao,
  id: string,
  corpo: {
    programa: ProgramaPorBase;
    premio: string | null;
    versaoEsperada: number;
    motivo: string;
  },
): Promise<Resultado<{ versao: number; vigenteDesde: string }>> {
  let r: Response;
  try {
    r = await fetch(`/api/produtos/${id}/premio-base`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok)
    return { ok: true, dados: (await r.json()) as { versao: number; vigenteDesde: string } };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  if (r.status === 400) return { ok: false, falha: { tipo: 'invalido', mensagem } };
  if (r.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

/** Define (ou tira, com null) o PMC do cadastro — o único teto de venda (decisão do dono 25/09). */
export async function definirPmcDoCadastro(
  sessao: Sessao,
  id: string,
  pmc: string | null,
  motivo: string,
): Promise<{ ok: true; pmc: string | null } | { ok: false; mensagem: string }> {
  let r: Response;
  try {
    r = await fetch(`/api/produtos/${id}/pmc`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify({ pmc, motivo }),
    });
  } catch {
    return {
      ok: false,
      mensagem: 'Sem conexão — não dá para saber se salvou. Recarregue e confira.',
    };
  }
  if (r.ok) return { ok: true, pmc: ((await r.json()) as { pmc: string | null }).pmc };
  let mensagem = 'Não foi possível salvar o PMC.';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? mensagem;
  } catch {
    // sem corpo
  }
  return { ok: false, mensagem };
}

/* Ações do produto (dono, 28/09): colocar no Paradão e lançar lote de pré-vencido daqui mesmo. */
async function escreverSimples(
  sessao: Sessao,
  caminho: string,
  corpo: unknown,
): Promise<{ ok: true } | { ok: false; mensagem: string }> {
  let r: Response;
  try {
    r = await fetch(caminho, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo),
    });
  } catch {
    return { ok: false, mensagem: 'Sem conexão. Confira antes de repetir.' };
  }
  if (r.ok) return { ok: true };
  let m = '';
  try {
    m = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  return {
    ok: false,
    mensagem:
      m || (r.status >= 500 ? 'Não deu para confirmar. Recarregue e confira.' : 'Não foi aceito.'),
  };
}
export const colocarNoParadao = (sessao: Sessao, productId: string, motivo: string) =>
  escreverSimples(sessao, `/api/paradao/${productId}`, { motivo: motivo || null });
export const lancarLoteDoProduto = (
  sessao: Sessao,
  productId: string,
  lote: { codigo: string | null; validade: string; quantidade: string | null },
) => escreverSimples(sessao, `/api/pre-vencidos/${productId}/lotes`, lote);
